import { supabase } from '../../lib/supabase'
import type { AvailabilityKind } from '../agent/availabilityStore'
import type { AvailabilityExceptionRecord, AvailabilityRuleRecord } from './availabilityOverlay'

export type TeamAvailability = {
  rules: AvailabilityRuleRecord[]
  exceptions: AvailabilityExceptionRecord[]
}

type RuleRow = {
  employee_id: string
  weekday: number
  start_time: string | null
  end_time: string | null
  kind: AvailabilityKind
  effective_from: string
  effective_to: string | null
}

type ExceptionRow = {
  employee_id: string
  start_date: string
  end_date: string
  start_time: string | null
  end_time: string | null
  kind: AvailabilityKind
}

const hourMinute = (value: string | null) => (value ? value.slice(0, 5) : undefined)

const dayBefore = (date: string) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10)
}

/**
 * The availability the signed-in supervisor (or administrator) can read, for
 * the dates the board shows: every recurring rule still in effect on or after
 * `from`, and every approved exception reaching `from` or later. Both reach back
 * one day, since a window that ran past midnight from the day before carries
 * into `from`. Row-level security limits both to the supervisor's own teams.
 */
export async function getTeamAvailability(from: string): Promise<TeamAvailability> {
  const reach = dayBefore(from)
  const [rules, exceptions] = await Promise.all([
    supabase
      .from('availability_rules')
      .select('employee_id, weekday, start_time, end_time, kind, effective_from, effective_to')
      .or(`effective_to.is.null,effective_to.gte.${reach}`),
    supabase
      .from('availability_exceptions')
      .select('employee_id, start_date, end_date, start_time, end_time, kind')
      .eq('state', 'approved')
      .gte('end_date', reach),
  ])

  return {
    rules: rules.error || !rules.data
      ? []
      : (rules.data as RuleRow[]).map((row) => ({
          employeeId: row.employee_id,
          weekday: row.weekday,
          startTime: hourMinute(row.start_time),
          endTime: hourMinute(row.end_time),
          kind: row.kind,
          effectiveFrom: row.effective_from,
          effectiveTo: row.effective_to ?? undefined,
        })),
    exceptions: exceptions.error || !exceptions.data
      ? []
      : (exceptions.data as ExceptionRow[]).map((row) => ({
          employeeId: row.employee_id,
          startDate: row.start_date,
          endDate: row.end_date,
          startTime: hourMinute(row.start_time),
          endTime: hourMinute(row.end_time),
          kind: row.kind,
        })),
  }
}
